import { environment } from '../../environments/environment.prod.js'
import { authService } from '../auth/auth-service.js'

const BASE = environment.api.baseUrl

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = authService.getAccessToken()
  if (!token) throw new Error('Not authenticated')

  return fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  })
}

export type ReportReason = 'spam' | 'inappropriate' | 'harassment' | 'misinformation' | 'other'

export interface ReportResponse {
  success: boolean
  reportId?: string
  error?: string
}

export interface BlockResponse {
  success: boolean
  sharedTeams?: SharedMembership[]
  sharedPlans?: SharedMembership[]
  error?: string
}

export interface SharedMembership {
  entityId: string
  entityName: string
  role: string
}

export interface BlockedUser {
  userId: string
  email?: string
  blockedAt: string
}

export async function reportContent(
  entityType: 'team' | 'user',
  entityId: string,
  reason: ReportReason,
  details?: string
): Promise<ReportResponse> {
  const res = await authFetch('/api/reports', {
    method: 'POST',
    body: JSON.stringify({ entityType, entityId, reason, details }),
  })
  if (!res.ok) {
    return { success: false, error: `HTTP ${res.status}: ${res.statusText}` }
  }
  return res.json()
}

export async function blockUser(userId: string): Promise<BlockResponse> {
  const res = await authFetch('/api/users/block', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  })
  if (!res.ok) {
    return { success: false, error: `HTTP ${res.status}: ${res.statusText}` }
  }
  return res.json()
}

export async function unblockUser(userId: string): Promise<{ success: boolean }> {
  const res = await authFetch(`/api/users/block/${userId}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    return { success: false }
  }
  return res.json()
}

export async function flagFieldLogEntry(
  planId: string,
  entryId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  const res = await authFetch(`/api/plans/${planId}/field-log/${entryId}/flag`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  if (!res.ok) {
    return { success: false, error: `HTTP ${res.status}: ${res.statusText}` }
  }
  return res.json()
}

export async function getBlockedUsers(): Promise<BlockedUser[]> {
  const res = await authFetch('/api/users/blocked')
  if (!res.ok) return []
  const data = await res.json()
  return data.blocked ?? []
}
